/**
 * The subscription clock.
 *
 * A paid plan is a plan plus a date it stops. This module owns the one decision
 * that follows from that: given where the account is now, has the period ended,
 * and if so does it roll forward, fall back to Free, or does somebody else have
 * to be asked.
 *
 * It is a pure function on purpose. Billing bugs are the kind that get noticed
 * by the customer rather than by you, so the rule has to be testable without a
 * database, a clock, or a network call to Polar.
 *
 * Evaluation is lazy: the caller applies this on the next request the account
 * makes, the same way src/worker/usage.ts rolls the monthly counters. A cron that
 * has to run for a customer's plan to be correct is a cron whose failure looks
 * exactly like a customer who is not paying.
 *
 * ## Who owns the renewal
 *
 * That depends on `provider`, and the distinction is the whole reason this file
 * grew a second path:
 *
 * - `polar` — Polar holds the card and decides when the period rolls. This
 *   module must never advance the date on its own, because doing so would extend
 *   a plan that Polar has stopped charging for. When the date passes it returns
 *   `stale`, which means "ask Polar", not "renew".
 * - `manual` — set by hand through scripts/set-plan.ts, or granted by the mock
 *   checkout. Nothing external is tracking it, so the local clock is the only
 *   clock and auto-renew rolls it forward.
 * - `none` — Free.
 */

import type { PlanId } from './plans';

/** One billing period. Whole days, so a period never drifts by a leap second. */
export const PERIOD_MS = 30 * 86_400_000;

/** How close to the end of a period we start saying so in the interface. */
export const RENEWAL_NOTICE_DAYS = 7;

/**
 * How long a Polar-backed plan survives past its own end date.
 *
 * Webhooks arrive late, get retried, and occasionally get lost behind a deploy.
 * Locking a paying customer out of their evidence bank because a notification
 * was slow is a far worse failure than three extra days of a plan that has
 * genuinely ended — the first loses the customer, the second costs nothing that
 * matters. Reconciliation against Polar's API closes the window in practice; this
 * is only the bound on how wrong it can get if that call also fails.
 */
export const GRACE_MS = 3 * 86_400_000;

/** Who is responsible for advancing the period. See the note at the top. */
export type SubscriptionProvider = 'none' | 'manual' | 'polar';

export interface SubscriptionState {
  plan: PlanId;
  /** End of the current paid period, epoch ms. `null` on Free. */
  planUntil: number | null;
  autoRenew: boolean;
  provider: SubscriptionProvider;
}

/**
 * What resolving decided.
 *
 * `stale` is the only one that is not a conclusion: it means the period ended on
 * a plan this module does not control, and the caller has to reconcile with
 * Polar before anything is written. Treating it as a renewal would give the plan
 * away; treating it as a lapse would take away a plan that is probably still
 * paid for.
 */
export type SubscriptionAction = 'none' | 'renewed' | 'lapsed' | 'stale';

export interface ResolvedSubscription extends SubscriptionState {
  action: SubscriptionAction;
  /** Periods rolled forward. More than one means the account was away a while. */
  periodsRenewed: number;
}

function freeState(action: SubscriptionAction, periodsRenewed = 0): ResolvedSubscription {
  return {
    plan: 'free',
    planUntil: null,
    autoRenew: false,
    provider: 'none',
    action,
    periodsRenewed,
  };
}

/** Anything with a plan and an end date. Enough to say how long is left. */
export type PeriodView = Pick<SubscriptionState, 'plan' | 'planUntil'>;

/**
 * Where the subscription actually stands at `now`.
 *
 * For `manual` plans the roll-forward advances in **whole periods** until it is
 * in the future. An account that went quiet for three months and comes back with
 * auto-renew on lands on its original day of the month, instead of having its
 * billing date silently shifted to whenever it happened to log in.
 */
export function resolveSubscription(state: SubscriptionState, now: number): ResolvedSubscription {
  // Free has no clock. A row that says 'free' with a stale date left on it is
  // still free — the plan column is what is sold, the date only bounds it.
  if (state.plan === 'free') return freeState('none');

  // A paid plan with no end date should not exist. Treating it as Free is the
  // safe direction to be wrong in: it under-serves rather than gives away.
  if (state.planUntil === null) return freeState('lapsed');

  if (now < state.planUntil) {
    return { ...state, action: 'none', periodsRenewed: 0 };
  }

  // Polar's clock, not ours. Hand it back unchanged and let the caller ask.
  if (state.provider === 'polar') {
    return { ...state, action: 'stale', periodsRenewed: 0 };
  }

  if (!state.autoRenew) return freeState('lapsed');

  let until = state.planUntil;
  let periods = 0;
  while (until <= now) {
    until += PERIOD_MS;
    periods += 1;
  }

  return {
    plan: state.plan,
    planUntil: until,
    autoRenew: true,
    provider: state.provider,
    action: 'renewed',
    periodsRenewed: periods,
  };
}

/**
 * Whether a Polar plan whose date has passed should still be honoured.
 *
 * Read by the caller after a reconciliation attempt fails — if Polar cannot be
 * reached, this decides between keeping the customer working and cutting them
 * off. Inside the window, keep them working.
 */
export function withinGrace(state: PeriodView, now: number): boolean {
  if (state.planUntil === null) return false;
  return now < state.planUntil + GRACE_MS;
}

/** Days left in the current period, rounded up. `null` on Free. */
export function daysRemaining(state: PeriodView, now: number): number | null {
  if (state.plan === 'free' || state.planUntil === null) return null;
  return Math.max(0, Math.ceil((state.planUntil - now) / 86_400_000));
}

/**
 * Whether the interface should be saying something about the renewal.
 *
 * Two different messages hang off this: "renews on the 14th" when it will, and
 * "ends on the 14th" when it will not. Both are worth showing near the end of a
 * period; neither is worth showing on day three.
 */
export function isNearRenewal(state: PeriodView, now: number): boolean {
  const days = daysRemaining(state, now);
  return days !== null && days <= RENEWAL_NOTICE_DAYS;
}

/** The state a fresh purchase produces on a locally-tracked plan. */
export function startSubscription(
  plan: PlanId,
  now: number,
  provider: SubscriptionProvider = 'manual',
): SubscriptionState {
  return { plan, planUntil: now + PERIOD_MS, autoRenew: true, provider };
}

/**
 * Cancelling a locally-tracked plan.
 *
 * `plan_until` is deliberately untouched. Somebody who cancels on day two paid
 * for thirty and keeps them; revoking on the spot would be charging for a month
 * and delivering two days of it, and it is the kind of thing a customer tells
 * other people about.
 *
 * Polar-backed plans do not come through here — cancelling one is a call to
 * Polar, and the answer comes back as a webhook. See src/worker/polar.ts.
 */
export function cancelSubscription(state: SubscriptionState): SubscriptionState {
  return { ...state, autoRenew: false };
}

/** Turning auto-renew back on, which is only meaningful before the period ends. */
export function resumeSubscription(state: SubscriptionState, now: number): SubscriptionState {
  if (state.planUntil === null || state.planUntil <= now) return state;
  return { ...state, autoRenew: true };
}

/* -------------------------------------------------------------------------- */
/* Polar                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A Polar subscription, reduced to the four things entitlement depends on.
 *
 * Deliberately not Polar's own type: this is the boundary where their vocabulary
 * stops and ours begins, and it is a pure function away from the network so the
 * table of statuses below can be tested without a fixture of their API.
 */
export interface PolarSubscriptionView {
  /** Polar's status enum, passed through as a string. */
  status: string;
  /** `current_period_end`, epoch ms. `null` if Polar did not send one. */
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  /** Which of our plans the purchased product maps to. */
  plan: PlanId;
}

/**
 * Statuses that keep the customer working.
 *
 * `past_due` is on the list on purpose. Polar is still retrying the card, and a
 * failed renewal at 3am should not lock somebody out of the evidence bank they
 * need at 9am — Polar sends `subscription.revoked` when it gives up, and that is
 * the event that actually takes the plan away.
 *
 * `canceled` is not on the list, and does not need to be: a customer who cancels
 * keeps `active` until the period ends, which is where the date does the work.
 */
const ENTITLED_STATUSES = new Set(['active', 'trialing', 'past_due']);

/** Whether this Polar subscription should be granting a paid plan right now. */
export function polarGrantsAccess(view: PolarSubscriptionView): boolean {
  return ENTITLED_STATUSES.has(view.status) && view.currentPeriodEnd !== null;
}

/**
 * The account state a Polar subscription implies.
 *
 * `autoRenew` is the inverse of `cancel_at_period_end`, which is the same fact
 * told from the other side: Polar records the intention to stop, the settings
 * screen shows whether it will continue.
 */
export function fromPolarSubscription(view: PolarSubscriptionView): SubscriptionState {
  if (!polarGrantsAccess(view)) {
    return { plan: 'free', planUntil: null, autoRenew: false, provider: 'none' };
  }
  return {
    plan: view.plan,
    planUntil: view.currentPeriodEnd,
    autoRenew: !view.cancelAtPeriodEnd,
    provider: 'polar',
  };
}
